<script setup lang="ts">
import type { ContentNavigationItem } from '@nuxt/content'

const colorMode = useColorMode()
const route = useRoute()

const color = computed(() => colorMode.value === 'dark' ? '#020618' : '#ffffff')

// Kanonische Form ist konsequent die www-Variante (siehe schemaOrg.ts). Jede Seite
// kann das per eigenem `useHead({ link: [{ rel: 'canonical', ... }] })` überschreiben,
// unhead dedupliziert `rel="canonical"` automatisch zugunsten der spezifischeren Angabe.
const canonicalUrl = computed(() => `${SITE_URL}${route.path.replace(/\/+$/, '') || '/'}`)

useHead({
  meta: [
    { charset: 'utf-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { key: 'theme-color', name: 'theme-color', content: color }
  ],
  link: [
    { rel: 'icon', href: '/favicon.ico' },
    { rel: 'canonical', href: canonicalUrl }
  ],
  htmlAttrs: {
    lang: 'de-CH'
  }
})

useSeoMeta({
  titleTemplate: '%s - Web Professionals',
  ogUrl: canonicalUrl,
  ogLocale: 'de_CH',
  ogSiteName: 'Web Professionals',
  ogType: 'website',
  ogImage: `${SITE_URL}/og-default.png`,
  ogImageAlt: 'Web Professionals – Deine Ausbildung für digitale Souveränität',
  twitterCard: 'summary_large_image'
})

useJsonLd({ '@graph': [organizationNode(), websiteNode()] }, 'schema-org')

function applyNavTitle(items: ContentNavigationItem[]): ContentNavigationItem[] {
  return items.map(item => ({
    ...item,
    title: (item as ContentNavigationItem & { titleNavigation?: string }).titleNavigation || item.title,
    children: item.children ? applyNavTitle(item.children) : undefined
  }))
}

const { data: navigation } = await useAsyncData(
  'navigation',
  () => queryCollectionNavigation('kurse', ['titleNavigation']),
  {
    transform: (data) => {
      const items = applyNavTitle(data.find(item => item.path === '/kurs')?.children || [])
      const webDev = items.find(item => item.path === '/kurs/web-dev')
      if (webDev) {
        webDev.children = [
          ...(webDev.children ?? []),
          { path: '/lehrgang/coden-mit-ki-einstieg', title: 'KI-Coding: Einstieg', icon: 'i-lucide-bot' },
          { path: '/lehrgang/foundation', title: 'Web Foundation', icon: 'i-lucide-rocket' },
          { path: '/lehrgang/developer', title: 'Web Developer', icon: 'i-lucide-code-2' }
        ]
      }
      return items
    }
  }
)
const { data: files } = useLazyAsyncData('search', () => queryCollectionSearchSections('kurse'), {
  server: false
})

const links = [{
  label: 'Docs',
  icon: 'i-lucide-book',
  to: '/kurs/web-dev'
}, {
  label: 'Pricing',
  icon: 'i-lucide-credit-card',
  to: '/pricing'
}, {
  label: 'Blog',
  icon: 'i-lucide-pencil',
  to: '/blog'
}, {
  label: 'Changelog',
  icon: 'i-lucide-history',
  to: '/changelog'
}]

provide('navigation', navigation)
</script>

<template>
  <UApp>
    <NuxtLoadingIndicator />

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <ClientOnly>
      <LazyUContentSearch
        :files="files"
        shortcut="meta_k"
        :navigation="navigation"
        :links="links"
        :fuse="{ resultLimit: 42 }"
      />
    </ClientOnly>
  </UApp>
</template>
